// Error toasts are the central analytics hook for handled validation blocks, but their two text
// fields are not equally safe. Titles are developer-authored literals; messages are built from API
// responses (`extractApiErrorMessage`, `result.error`) and from account, user and rule names, so
// they can carry customer or internal business data. Analytics therefore never sees the toast text:
// the title is looked up here and only the mapped code below is emitted. Every code is a literal in
// this file, so no runtime-derived string can leave the browser. Unknown titles bucket to
// UNCLASSIFIED_ERROR_CODE — add an entry here when a new error class needs its own funnel.

export const UNCLASSIFIED_ERROR_CODE = 'unclassified';

// The analytics-safe category for a failed send. Derived from the HTTP status only, so it can
// never carry the server's reason string. `no-response` means the request never got an HTTP
// response the browser could read (typically an expired edge session), as distinct from a 5xx;
// `offline` is that same failure while the browser reports it has no connection.
export type SendFailureCode = 'conflict' | 'retryable' | 'blocked' | 'no-response' | 'offline';

// Multi-family send gates are admitted by a constrained shape. Other server codes must be named
// explicitly, so an account name, email, or API error sentence can never masquerade as telemetry.
// Anything else is discarded and the caller falls back to the title lookup.
const GATE_CODE_PATTERN = /^MFG_G\d{2}_[A-Z0-9_]{1,48}$/;
const SAFE_SERVER_CODES = new Set([
  'QUOTE_EXECUTION_ACTIVE',
  'POLICY_BLOCKED',
  'MFG_SCHEDULED_CHANGES_ACK_REQUIRED',
  'SCHEDULED_CHANGES_STALE',
  'SCHEDULED_CHANGES_ELEVATED_ROLE',
  'SCHEDULED_CHANGES_UNAVAILABLE',
]);

export function analyticsSafeCode(code: unknown): string | null {
  if (typeof code !== 'string') return null;
  return GATE_CODE_PATTERN.test(code) || SAFE_SERVER_CODES.has(code) ? code : null;
}

// The quote wizard's own gates (Continue, Submit, the step pills) report one of these when a rep
// runs into them; a Line Items issue carries its own code from lineItemsValidation. One code per
// rule, so a Terms refusal reads as the rule that held it rather than the generic 'validation'.
export const WIZARD_GATE_CODES = [
  'account-required',
  'quote-type-required',
  'new-customer-quote-type',
  'contact-name-required',
  'contact-email-required',
  'new-customer-contact-required',
  'subscriptions-loading',
  'poc-days-out-of-range',
  'contract-term-invalid',
  'contract-term-whole-years',
  'starts-on-invalid',
  'renewal-coterm-refused',
  'coterm-target-required',
  'align-anchor-required',
  'align-anchor-unavailable',
  'shared-billing-date-invalid',
  'new-term-renewal-conflict',
  'new-term-reduction-conflict',
  'keep-current-term-required',
  'new-term-minimum',
  'amendment-target-required',
  'amendment-target-missing',
  'amendment-family-blocked',
  'option-selection-invalid',
  'quote-has-no-changes',
  'product-family-mismatch',
  'renewal-blocked',
  'coterm-send-refused',
  'tiered-price-send-refused',
] as const;

export type WizardGateCode = (typeof WIZARD_GATE_CODES)[number];

// A Map, not an object literal: an object lookup for a title like 'Constructor' or 'toString'
// resolves through Object.prototype and returns a function instead of a code, which `??` does not
// catch. Map keys have no prototype chain.
const TITLE_CODES = new Map<string, string>(Object.entries({
  'validation': 'validation',
  'error': 'error',
  'request failed': 'request-failed',
  'retry failed': 'retry-failed',
  'company not found': 'company-not-found',
  'product family mismatch': 'product-family-mismatch',
  'rejected': 'approval-rejected',

  'save failed': 'save-failed',
  'failed to save': 'save-failed',
  'submit failed': 'submit-failed',
  'submit blocked': 'submit-blocked',
  'delete failed': 'delete-failed',
  'cancel failed': 'cancel-failed',
  'recall blocked': 'recall-blocked',
  'recall failed': 'recall-failed',
  'mark as lost failed': 'mark-lost-failed',
  'discard failed': 'discard-failed',
  'download failed': 'download-failed',
  'link unavailable': 'link-unavailable',

  'not compatible with this subscription': 'tier-not-applicable',
  'fixed poc offering': 'poc-fixed-offering',
  'not sold on this subscription': 'tier-cadence',

  'send blocked': 'send-blocked',
  'send failed — try again': 'send-retryable',
  'send not completed': 'send-conflict',
  "couldn't reach meridian to send": 'send-no-response',
  "you're offline — couldn't send": 'send-offline',
  'resend blocked': 'resend-blocked',
  'resend failed — try again': 'resend-retryable',
  'resend not completed': 'resend-conflict',
  "couldn't reach meridian to resend": 'resend-no-response',
  "you're offline — couldn't resend": 'resend-offline',
  'failed to send': 'test-email-failed',

  'could not link': 'account-link-failed',
  'could not link parent': 'parent-link-failed',
  'could not unlink parent': 'parent-unlink-failed',

  'sync failed': 'sync-failed',
  'sync trigger failed': 'sync-trigger-failed',
  'import trigger failed': 'import-trigger-failed',
  'bootstrap trigger failed': 'bootstrap-trigger-failed',
  'match trigger failed': 'match-trigger-failed',
  'tiebreak trigger failed': 'tiebreak-trigger-failed',
}));

export function errorCodeForTitle(title: unknown): string {
  if (typeof title !== 'string') return UNCLASSIFIED_ERROR_CODE;
  return TITLE_CODES.get(title.trim().toLowerCase()) ?? UNCLASSIFIED_ERROR_CODE;
}
